import { useEffect, useRef } from 'react'
import { usePlan } from '../plan/usePlan'

// Conversation — the transcript, the suggestion chips and the composer.
//
// Extracted so the assistant is literally the same thing wherever it is: the
// floating panel, the pinned column, and the console's Chat tab all render THIS.
// Before, the same conversation existed as three different implementations,
// which is why the judge read the product as having no single voice. One
// implementation is the only way that stays true as it changes.
export function Conversation({ compact = false }: { compact?: boolean }) {
  const p = usePlan()
  const logRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)

  // The composer starts one line tall and grows with what you type — a fixed
  // box makes you write into a slot whose end you cannot see. It stops at a
  // third of the container so the input can never squeeze out the conversation.
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    const cap = Math.max(88, Math.round((rootRef.current?.clientHeight ?? 460) / 3))
    el.style.height = `${Math.min(el.scrollHeight, cap)}px`
    el.style.overflowY = el.scrollHeight > cap ? 'auto' : 'hidden'
  }, [p.input])

  // Keep the newest turn in view as it arrives.
  useEffect(() => {
    const el = logRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [p.chat.length])

  return (
    <div className={`convo-body${compact ? ' compact' : ''}`} ref={rootRef}>
      <div className="ap-log" ref={logRef} role="log" aria-label="Conversation" tabIndex={0}>
        {p.chat.map((c) => (
          <div key={c.id} className={`ap-turn ${c.role}`}>
            <div className={`bubble ${c.role}`}>{c.text}</div>
            {c.applied && c.applied.length > 0 ? (
              <div className="turn-applied" title="Applied to the plan">
                <span className="turn-applied-mark">✓ merged</span>
                {c.applied.map((a, i) => (
                  <span key={i} className="chat-chip" title={a.summary}>
                    {a.kind.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            ) : null}
            {(c.pending ?? []).map((pa, i) => (
              <div key={i} className="turn-card pending-card">
                <div className="turn-card-head static">
                  <span className="turn-card-title">Confirm needed</span>
                </div>
                <div className="turn-card-body">
                  <p className="pending-sum">{pa.summary}</p>
                  <div className="pending-actions">
                    <button className="chat-confirm" onClick={() => p.confirmPending(c.id, pa)} disabled={p.busy}>
                      Confirm
                    </button>
                    <button className="chat-dismiss" onClick={() => p.dismissPending(c.id, pa)}>
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
        <div ref={p.chatEndRef} />
      </div>


      <div className="ap-composer">
        <textarea
          ref={inputRef}
          rows={1}
          value={p.input}
          placeholder="Tell me what to change…"
          aria-label="Tell the assistant what to change"
          onChange={(e) => p.setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              p.sendChat()
            }
          }}
        />
        <button className="ap-send" onClick={p.sendChat} disabled={p.busy}>
          {p.busy ? 'Working…' : 'Send'}
        </button>
      </div>
    </div>
  )
}
